import { TRPC_ENDPOINT, API_URLS } from '@idle-depths/shared';
import { QueryClient } from '@tanstack/react-query';
import { httpBatchLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';

import type { AppRouter } from '../../../server/src/trpc/types';

/**
 * tRPC React hooks.
 *
 * @example
 * ```tsx
 * const { data } = trpc.health.check.useQuery();
 * ```
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * Get the API base URL from environment or default.
 *
 * Priority:
 * 1. VITE_API_URL environment variable
 * 2. Development default (localhost:8787 for wrangler)
 * 3. Production default based on hostname
 */
function getApiBaseUrl(): string {
  // Check for environment variable first (set by CI/CD)
  const envUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (envUrl) {
    return envUrl;
  }

  // Default to wrangler dev server in development
  if (import.meta.env.DEV) {
    return API_URLS.development;
  }

  // In production, determine API URL from current hostname
  const hostname = window.location.hostname;

  if (hostname.includes('staging')) {
    return API_URLS.staging;
  }

  if (hostname.includes('beta')) {
    return API_URLS.beta;
  }

  // Production
  return API_URLS.production;
}

/**
 * Create the tRPC client instance.
 */
export function createTRPCClient(): ReturnType<typeof trpc.createClient> {
  const baseUrl = getApiBaseUrl();

  return trpc.createClient({
    links: [
      httpBatchLink({
        url: `${baseUrl}${TRPC_ENDPOINT}`,
        headers: () => ({
          // Add any default headers here
        }),
      }),
    ],
  });
}

/**
 * Create a configured QueryClient for React Query.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Stale time of 30 seconds
        staleTime: 30 * 1000,
        // Retry failed requests up to 3 times
        retry: 3,
        // Retry with exponential backoff
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        // Refetch on window focus for fresh data
        refetchOnWindowFocus: true,
      },
      mutations: {
        // Retry mutations once on failure
        retry: 1,
      },
    },
  });
}

/**
 * Export the router type for type inference.
 */
export type { AppRouter };
